import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, Send, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Survey() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ liked: "", improve: "", support: "", comments: "" });
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#EAF7F4]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  // optional feedback for pilot parents; never blocks access
  if (user.is_admin || !user.survey_prompt) return <Navigate to="/parent" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please give a star rating."); return; }
    setBusy(true);
    try {
      await api.post("/survey", { rating, ...form });
      await refresh();
      toast.success("Thank you for your feedback!");
      navigate("/parent");
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); setBusy(false); }
  };

  const Field = ({ label, k }) => (
    <div>
      <label className="block font-semibold text-slate-700 mb-1.5 text-sm">{label}</label>
      <textarea value={form[k]} onChange={(ev) => setForm((f) => ({ ...f, [k]: ev.target.value }))} rows={2}
        data-testid={`survey-${k}`} className="w-full rounded-xl border-2 border-slate-900 p-3 outline-none focus:ring-2 focus:ring-blue-300" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-parent text-slate-900 py-10 px-5">
      <div className="max-w-xl mx-auto nb-card bg-white p-7">
        <h1 className="text-2xl font-bold">Quick feedback (optional)</h1>
        <p className="text-slate-600 mt-2 text-sm">Thanks for being part of the Anou Appran pilot! If you have 1 minute, share your feedback — it helps us make the app better for every child.</p>
        <button type="button" onClick={() => navigate("/parent")} className="mt-2 text-sm font-semibold text-blue-600 underline" data-testid="survey-skip">Skip for now / Plus tard</button>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5 text-sm">How would you rate it so far?</label>
            <div className="flex gap-1.5" data-testid="survey-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`survey-star-${n}`} className="p-1">
                  <Star size={30} className={n <= rating ? "text-yellow-400" : "text-slate-300"} fill={n <= rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>
          <Field label="What did you like?" k="liked" />
          <Field label="What can be improved?" k="improve" />
          <Field label="What further support would you need?" k="support" />
          <Field label="Any other comments?" k="comments" />
          <button disabled={busy} data-testid="survey-submit" className="w-full rounded-full bg-blue-600 text-white font-semibold py-3 border-2 border-slate-900 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Send feedback
          </button>
        </form>
      </div>
    </div>
  );
}
